import { useEffect, useRef, useState } from 'react';
import { api } from '../../../api';
import type { BasilHttp } from '../write/writeTypes';
import { EMPTY_BOOK_NAMES, readBookNames } from './readBookNames';
import type { BookNames } from './readBookNames';

/** The words, and whether the one-shot read has finished deciding what they are. */
export interface BookNamesRead {
  names: BookNames;
  /** true once the words are fixed: by the read answering, or because there was nothing to read. */
  settled: boolean;
}

export interface UseBookNamesOptions {
  /** the modal is open; a closed modal reads nothing. */
  enabled: boolean;
  /** the http seam; defaults to the page's axios instance. Tests pass a mock. */
  http?: BasilHttp;
}

/** Nothing read yet. One frozen value, so a render that changes nothing changes no identity. */
const NOTHING: BookNamesRead = Object.freeze({ names: EMPTY_BOOK_NAMES, settled: false }) as BookNamesRead;

/**
 * The book's words for its characters, moods and locations, read once per open (Basil v0.1.1).
 *
 * ORDERING. The request goes out with the episode read, not after it, so the first decompile
 * normally already has the words. When it lands second the document simply re-decompiles with
 * them: `useBasilDocument` binds its reset to the read and not to the words, so a writer who has
 * already typed keeps their text, and the sidecar map the compiler aligns on does not depend on
 * the words at all. Nothing waits for this hook — a slow list costs the script its names for a
 * moment, never its text.
 *
 * A newer open abandons an older read: the effect's own latch is cleared on cleanup, so a reply to
 * a closed modal is dropped rather than written into a later one.
 *
 * Fail-open throughout: `readBookNames` never rejects, a list that could not be read supplies no
 * words for its domain, and nothing here is ever shown to a writer or reaches the write path.
 */
export function useBookNames(bookId: number, options: UseBookNamesOptions): BookNamesRead {
  const { enabled, http } = options;
  const [state, setState] = useState<BookNamesRead>(NOTHING);
  const httpRef = useRef<BasilHttp>(http || (api as unknown as BasilHttp));

  httpRef.current = http || (api as unknown as BasilHttp);

  useEffect(() => {
    if (!enabled) {
      setState(NOTHING);

      return undefined;
    }
    let live = true;

    setState(NOTHING);
    readBookNames(httpRef.current, bookId)
      .then((names) => {
        if (live) {
          setState({ names, settled: true });
        }
      })
      .catch(() => {
        if (live) {
          setState({ names: EMPTY_BOOK_NAMES, settled: true });
        }
      });

    return () => {
      live = false;
    };
  }, [enabled, bookId]);

  return state;
}
